import { IconActionArrowShapeTurnUpLeft16 } from '@cian/ui-kit-design-tokens/icons';
import { clsx } from 'clsx';
import * as React from 'react';

import styles from './ChatMessageSwipeReply.css';
import { getSwipeIconOpacity } from './utils';
import { useMessageSwipe, useMessageSwipeOnboarding } from '../../hooks/useMessageSwipe';
import { useReplyAvailability } from '../../hooks/useReplyAvailability';
import { EMessageType } from '../../types/messageType';
import { useMessageContext } from '../../utils/messageContext';
import { Caption } from '@cian/ui-kit';

interface IChatMessageSwipeReplyProps {
  children: React.ReactNode;
  bubbles?: React.ReactNode;
  isSwipeOnboardingTarget: boolean;
  onSwipe: VoidFunction;
}

export const ChatMessageSwipeReply: React.FC<IChatMessageSwipeReplyProps> = ({
  children,
  bubbles,
  isSwipeOnboardingTarget,
  onSwipe,
}) => {
  const { message, messageType, messageRef } = useMessageContext();
  const contentElementRef = React.useRef<HTMLDivElement>(null);
  const iconElementRef = React.useRef<HTMLDivElement>(null);
  const hintElementRef = React.useRef<HTMLDivElement>(null);

  const canReplyToMessage = useReplyAvailability(message);

  const { isOnboardingAnimationPlaying } = useMessageSwipeOnboarding({
    isSwipeOnboardingTarget,
    messageRef,
    contentElementRef,
    iconElementRef,
    hintElementRef,
  });

  const { offset, isSwiping, onClick, ...bind } = useMessageSwipe({
    disabled: !canReplyToMessage || isOnboardingAnimationPlaying,
    messageRef,
    onSwipe,
  });

  const iconOpacity = getSwipeIconOpacity(offset);

  return (
    <>
      <div
        className={clsx(
          styles['swipe'],
          (isSwiping || isOnboardingAnimationPlaying) && styles['swipe--active'],
          messageType === EMessageType.Outcoming && styles['swipe--outcoming'],
        )}
        onClick={onClick}
        data-testid="ChatMessageSwipeReply"
        {...bind}
      >
        <div
          ref={contentElementRef}
          className={styles['swipe__content']}
          style={{ transform: `translateX(${offset}px)` }}
        >
          {children}
          <div className={styles['swipe__trail']}>
            <div ref={iconElementRef} className={styles['swipe__icon']} style={{ opacity: iconOpacity }} aria-hidden>
              <IconActionArrowShapeTurnUpLeft16 color="icon-main-default" />
            </div>
            {(isSwipeOnboardingTarget || isOnboardingAnimationPlaying) && (
              <div
                ref={hintElementRef}
                className={styles['swipe__hint']}
                data-testid="ReplySwipeOnboardingHint"
                aria-hidden
              >
                <Caption whiteSpace="pre-line" color="text-main-default">
                  Сдвиньте сообщение,
                  <br />
                  чтобы ответить
                </Caption>
              </div>
            )}
          </div>
        </div>
      </div>
      {bubbles ? <div className={styles['swipe__bubbles']}>{bubbles}</div> : null}
    </>
  );
};
